export interface CanvasPoint {
  x: number;
  y: number;
}

export interface ContainRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Returns the centered content box produced by object-fit: contain.
 * Keeping video and annotation layout on this same rectangle prevents
 * asymmetric clipping and makes canvas coordinates portable to viewers.
 */
export const getContainedContentRect = (
  containerWidth: number,
  containerHeight: number,
  sourceWidth: number,
  sourceHeight: number
): ContainRect => {
  const safeContainerWidth = Math.max(containerWidth, 0);
  const safeContainerHeight = Math.max(containerHeight, 0);

  if (
    safeContainerWidth === 0 ||
    safeContainerHeight === 0 ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return {
      left: 0,
      top: 0,
      width: safeContainerWidth,
      height: safeContainerHeight,
    };
  }

  const scale = Math.min(
    safeContainerWidth / sourceWidth,
    safeContainerHeight / sourceHeight
  );
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;

  return {
    left: (safeContainerWidth - width) / 2,
    top: (safeContainerHeight - height) / 2,
    width,
    height,
  };
};

type CanvasPointerEvent = Pick<
  MouseEvent,
  "clientX" | "clientY" | "offsetX" | "offsetY"
>;

/**
 * Converts viewport pointer coordinates into the canvas backing-buffer space.
 * The screenboard canvas is commonly rendered at a responsive CSS size while
 * retaining the shared screen's native resolution for the outgoing stream.
 */
export const getCanvasPoint = (
  event: CanvasPointerEvent,
  canvas: HTMLCanvasElement
): CanvasPoint => {
  const rect = canvas.getBoundingClientRect();

  if (rect.width <= 0 || rect.height <= 0) {
    return { x: event.offsetX, y: event.offsetY };
  }

  return {
    x: (event.clientX - rect.left) * (canvas.width / rect.width),
    y: (event.clientY - rect.top) * (canvas.height / rect.height),
  };
};
